import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { DatabaseSchemaPageSelection } from 'apps/sigma2-ui/src/data-wizard/lib/useDatabaseSchemaPage';
import { useDatabaseSchemaPage } from 'apps/sigma2-ui/src/data-wizard/lib/useDatabaseSchemaPage';
import cn from 'classnames';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { DatabaseSchemaJsonEditor } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseSchemaJsonEditor';
import { DatabaseRevisionSchemaWithoutFilters } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema';
import { DatabaseSchemaBiasTaskEditor } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseSchemaBiasTaskEditor';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const DatabaseSchemaPage = () => {
  const {
    selectedDatabaseId,
    selectedOrganizationId,
    currentSelection,
    setCurrentSelection,
    selectionOptions,
    pageTitle,
    onLoadSchema,
    onSaveSchema,
    onPublishRevision,
    mostRecentErrorMessage,
    selectedUiNode,
    isSaving,
    revisionName,
    setRevisionName,
    isRevisionHidden,
    setIsRevisionHidden,
    currentSchemaToSave,
    onUpdateSchemaLocally,
    revisionStatus,
    loadedSchemaOptions,
    revisionId,
    dataRequirementWarnings,
  } = useDatabaseSchemaPage();

  const language = useLanguage();

  const navigate = useNavigateSafe();
  const bottomOfDivRef = React.useRef<HTMLDivElement>(null);

  const [isEditingName, setIsEditingName] = React.useState(false);

  const [showJsonEditor, setShowJsonEditor] = useState(false);

  const [showBiasTaskEditor, setShowBiasTaskEditor] = useState(false);

  useEffect(() => {
    onLoadSchema();
  }, [onLoadSchema]);

  const canRevisionBePublished = useMemo(() => {
    switch (revisionStatus) {
      case 'Published':
      case 'Publishing':
      case 'PublishingFailed':
      case 'PublishingRequested':
      case 'Archived':
      case null:
        return false;
      case 'Draft':
        return true;
    }
  }, [revisionStatus]);

  return (
    <AdminLayout currentPage={'DatabaseSchema'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${selectedOrganizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${selectedOrganizationId}/databases/edit/${selectedDatabaseId}`,
          },
          {
            label: 'Revisions',
            to: `/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions`,
          },
          {
            label: 'Schema',
            to: `/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${revisionId}/schema`,
          },
        ]}
      />
      {showJsonEditor && (
        <DatabaseSchemaJsonEditor
          currentModel={currentSchemaToSave}
          schema={DatabaseRevisionSchemaWithoutFilters}
          onSaved={onUpdateSchemaLocally}
          show={showJsonEditor}
          setShow={setShowJsonEditor}
        />
      )}

      {showBiasTaskEditor && (
        <DatabaseSchemaBiasTaskEditor
          currentSchemaToSave={currentSchemaToSave}
          onSaved={onUpdateSchemaLocally}
          show={showBiasTaskEditor}
          setShow={setShowBiasTaskEditor}
          uniqueValuesByColumn={loadedSchemaOptions?.uniqueValuesByColumn ?? []}
        />
      )}
      <Spacing size={'md'} direction={'vertical'}>
        <S2Typography
          variant="headline-3"
          translatedContent={language.dangerousUserInput({
            value: pageTitle,
          })}
        />

        {dataRequirementWarnings.length > 0 && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Data Requirement Warnings
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc list-inside space-y-1">
                    {dataRequirementWarnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <Spacing size={'sm'} direction={'horizontal'}>
          {isEditingName ? (
            <SimpleInput
              label={'Name'}
              value={revisionName}
              onChange={setRevisionName}
            />
          ) : (
            <S2Typography
              variant="headline-4"
              translatedContent={language.dangerousUserInput({
                value: revisionName ?? 'N/A',
              })}
            />
          )}

          {isEditingName ? (
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  setIsEditingName(false);
                },
              }}
              variant={'outline'}
              size={'small'}
            >
              <S2Typography
                variant={'headline-7'}
                translatedContent={language.save()}
              />
            </Button>
          ) : (
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  setIsEditingName(true);
                },
              }}
              variant={'outline'}
              size={'small'}
            >
              <S2Typography
                variant={'headline-7'}
                translatedContent={language.edit()}
              />
            </Button>
          )}
        </Spacing>

        <Spacing size={'sm'} direction={'horizontal'}>
          <S2Typography
            variant={'headline-7'}
            translatedContent={language.hidden()}
          />

          <Switch
            checked={isRevisionHidden}
            onChange={() => {
              setIsRevisionHidden(!isRevisionHidden);
            }}
          >
            <div></div>
          </Switch>
        </Spacing>

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              setShowJsonEditor(true);
            },
          }}
          text={'Edit JSON'}
        />

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              setShowBiasTaskEditor(true);
            },
          }}
          text={'Edit Bias Tasks'}
        />

        <Spacing size="justify-between" direction="horizontal">
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => {
                navigate(
                  `/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/transform`,
                );
              },
            }}
            text={'Back'}
          />

          <Spacing size="sm" direction="horizontal">
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  onSaveSchema();
                },
              }}
              disabled={isSaving}
              text={'Save'}
            />

            <Button
              behavior={{
                type: 'onClick',
                onClick: onPublishRevision,
              }}
              disabled={isSaving || !canRevisionBePublished}
              text={'Publish'}
            />
          </Spacing>
        </Spacing>
      </Spacing>

      <TabsRadioGroup
        currValue={currentSelection as string}
        values={selectionOptions}
        onChange={(value: string) => {
          setCurrentSelection(DatabaseSchemaPageSelection.parse(value));
        }}
        minWidth="auto"
        gap="md"
        underlineBgClass="bg-secondary"
        renderTab={(tab) => {
          const value = tab.value as {
            displayedValue: string;
            tabValue: string;
            automationTag: string;
          };
          return (
            <span className="w-min">
              <span
                className={cn('mx-1 font-sans text-lg font-semibold', {
                  'text-secondary': value.tabValue == currentSelection,
                  'text-gray-600': value.tabValue != currentSelection,
                })}
              >
                {value.displayedValue}
              </span>
            </span>
          );
        }}
      />

      {mostRecentErrorMessage && (
        <div className="text-red-500">
          {mostRecentErrorMessage.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      {selectedUiNode}
      <div ref={bottomOfDivRef} />
    </AdminLayout>
  );
};
